"use client";

import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Link from "next/link";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { Phone, Mail, ArrowRight, CheckCircle2, LogIn } from "lucide-react";
import Header from "@/components/layout/Header";
import apiClient from "@/api/config";

// --- API call ---
export const getEmailFromPhoneNumber = async (
  phoneNumber: string,
): Promise<string[]> => {
  const { data } = await apiClient.get<string[]>(
    `/public/getEmailFromPhoneNumber/${phoneNumber}`,
  );
  return data;
};

// --- Email masking util ---
const maskEmail = (email: string): string => {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  const visible = Math.max(2, Math.floor(local.length * 0.3));
  const start = local.slice(0, Math.ceil(visible / 2));
  const end = local.slice(-Math.floor(visible / 2));
  return `${start}${"*".repeat(local.length - start.length - end.length)}${end}@${domain}`;
};

// --- Validation Schema ---
const PhoneSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .matches(/^[0-9+\s\-()]{7,15}$/, "Enter a valid phone number")
    .required("Phone number is required"),
});

// --- Component ---
const ForgotEmailPage = () => {
  const router = useRouter();
  const [emails, setEmails] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  return (
    <>
      <Header />
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Section */}
        <div className="w-full lg:w-2/5 bg-[#1C1B4D] flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-12">
          <h1 className="font-bold text-[#FFF] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight">
            Recover your
          </h1>
          <h1
            className="font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mt-2 leading-tight"
            style={{
              backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            email address
          </h1>
          <p className="text-[#FFF]/60 mt-6 text-sm sm:text-base max-w-xs leading-relaxed">
            Enter the phone number linked to your account and we'll show you the
            emails registered with it.
          </p>

          {/* Decorative dots */}
          <div className="mt-12 flex gap-2">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className="block w-2 h-2 rounded-full"
                style={{
                  backgroundColor:
                    i === 0
                      ? "#EFAE33"
                      : i === 1
                        ? "#B97BFF"
                        : "rgba(255,255,255,0.2)",
                }}
              />
            ))}
          </div>
        </div>

        {/* Right Section */}
        <div className="w-full lg:w-3/5 flex justify-center items-center px-6 sm:px-10 py-12 bg-[#FFF]">
          <div className="w-full max-w-md">
            {!submitted ? (
              <>
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-[#1C1B4D]/8 flex items-center justify-center">
                    <Phone
                      className="w-7 h-7 text-[#1C1B4D]"
                      strokeWidth={1.5}
                    />
                  </div>
                </div>

                <h3 className="font-bold text-[#1C1B4D] text-2xl sm:text-3xl text-center mb-2">
                  Forgot Email?
                </h3>
                <p className="text-center text-[#6B7280] text-sm mb-8">
                  We'll look up all email addresses linked to your phone number.
                </p>

                <Formik
                  initialValues={{ phoneNumber: "" }}
                  validationSchema={PhoneSchema}
                  onSubmit={async (values, { setSubmitting }) => {
                    try {
                      const result = await getEmailFromPhoneNumber(
                        values.phoneNumber.replace(/\s/g, ""),
                      );
                      setEmails(result || []);
                      setSubmitted(true);
                    } catch (error: any) {
                      const msg =
                        error?.response?.data ||
                        "Could not find any account with that phone number.";
                      toast.error(
                        typeof msg === "string" ? msg : "Something went wrong.",
                      );
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form className="flex flex-col gap-5">
                      <div className="flex flex-col gap-2">
                        <label className="font-medium text-[#1C1B4D] text-sm">
                          Phone Number
                        </label>
                        <div className="relative">
                          <Phone
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]"
                            strokeWidth={1.5}
                          />
                          <Field
                            type="tel"
                            name="phoneNumber"
                            className={`w-full rounded-lg py-3 pl-10 pr-4 border text-sm focus:outline-none focus:ring-2 focus:ring-[#1C1B4D]/30 transition ${
                              errors.phoneNumber && touched.phoneNumber
                                ? "border-[#d30a0a] bg-[#FEE2E2]"
                                : "border-[#D1D5DB] bg-[#F9FAFB]"
                            }`}
                            placeholder="e.g. 08123456789"
                          />
                        </div>
                        <ErrorMessage
                          name="phoneNumber"
                          component="p"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 bg-[#1C1B4D] rounded-lg text-[#FFF] text-sm font-semibold py-3 hover:bg-[#1C1B4D]/90 disabled:opacity-50 transition mt-1"
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8z"
                              />
                            </svg>
                            Searching...
                          </>
                        ) : (
                          <>
                            Find My Email <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>

                      <Link
                        href="/sign-in"
                        className="text-sm text-center text-[#1C1B4D] hover:text-[#1C1B4D]/70 transition"
                      >
                        Back to Login
                      </Link>
                    </Form>
                  )}
                </Formik>
              </>
            ) : (
              /* Results State */
              <div className="flex flex-col items-center gap-6 animate-fade-in">
                <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center">
                  <CheckCircle2
                    className="w-7 h-7 text-green-500"
                    strokeWidth={1.5}
                  />
                </div>

                <div className="text-center">
                  <h3 className="font-bold text-[#1C1B4D] text-2xl sm:text-3xl mb-2">
                    Emails Found
                  </h3>
                  <p className="text-[#6B7280] text-sm">
                    {emails.length} email{emails.length > 1 ? "s are" : " is"}{" "}
                    linked to your phone number. Use one to sign in or reset
                    your password.
                  </p>
                </div>

                {/* Email list */}
                <div className="w-full space-y-3">
                  {emails.map((email, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl px-4 py-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#1C1B4D]/10 flex items-center justify-center shrink-0">
                        <Mail
                          className="w-4 h-4 text-[#1C1B4D]"
                          strokeWidth={1.5}
                        />
                      </div>
                      <span className="font-mono text-sm text-[#374151] tracking-wide select-none">
                        {maskEmail(email)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Info note */}
                <p className="text-xs text-[#9CA3AF] text-center leading-relaxed bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 w-full">
                  For security, emails are partially hidden. Use the one you
                  recognize to sign in, then reset your password if needed.
                </p>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <button
                    onClick={() => router.push("/sign-in")}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#1C1B4D] text-[#FFF] text-sm font-semibold py-3 rounded-xl hover:bg-[#1C1B4D]/90 transition"
                  >
                    <LogIn className="w-4 h-4" /> Go to Sign In
                  </button>
                  <button
                    onClick={() => router.push("/forgot-password")}
                    className="flex-1 flex items-center justify-center gap-2 border border-[#1C1B4D] text-[#1C1B4D] text-sm font-semibold py-3 rounded-xl hover:bg-[#1C1B4D]/5 transition"
                  >
                    Reset Password
                  </button>
                </div>

                <button
                  onClick={() => {
                    setSubmitted(false);
                    setEmails([]);
                  }}
                  className="text-sm text-[#9CA3AF] hover:text-[#6B7280] transition"
                >
                  Try a different number
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotEmailPage;
